import { FormSchema } from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import {
  NewQuestionGroup,
  QuestionFeature,
  QuestionType,
  SchemaFieldWithId,
} from "@stormwater-software/lib-inspection-templates";
import React, {
  PropsWithChildren,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import Loading from "../components/Shared/ResourceIndex/Loading";
import { pdfLayoutKey } from "../hooks/pdfLayout";
import { useCreateQuestionGroup } from "../hooks/questionGroups";
import { useQuestionTypes } from "../hooks/questionTypes";
import { PdfLayoutGroup } from "../utils/api/pdfLayout";
import { questionConfigApi } from "../utils/api/questionConfig";
import { questionGroupApi } from "../utils/api/questionGroup";

export type WarningMessages = "date" | "type";

export type IInspectionTemplateEditorProps = {
  questionTypes: Array<QuestionType>;
  templateId: number;
  groups: Array<PdfLayoutGroup>;
  refetchQuestionGroups: () => Promise<unknown>;
  addNewQuestionGroup: (
    data: NewQuestionGroup,
    event?: SyntheticEvent,
  ) => Promise<{ id: number }>;
  defaultSchema: FormSchema;
  mappedAndSortedQuestionGroups: Array<{
    id: number;
    order: number;
    name?: string;
  }>;
  swapQuestionGroups: (groupId: number, direction: string) => Promise<void>;
  isConditionGroupApplied: boolean;
  questionsWithOptions: Array<number>;
  questionConfigData: Array<any>;
  getNextQuestionGroupOrder: () => number;
  warnings: Array<WarningMessages>;
};

const throwError = () => {
  throw new Error("TemplateEditorProvider not loaded");
};

const Context = React.createContext<IInspectionTemplateEditorProps>({
  questionTypes: [],
  templateId: 0,
  groups: [],
  refetchQuestionGroups: throwError,
  addNewQuestionGroup: throwError,
  defaultSchema: {},
  mappedAndSortedQuestionGroups: [],
  swapQuestionGroups: throwError,
  isConditionGroupApplied: false,
  questionsWithOptions: [],
  questionConfigData: [],
  getNextQuestionGroupOrder: throwError,
  warnings: [],
});

const buildSchema = (
  questionTypes: Array<QuestionType>,
  defaultSchemaData: Array<SchemaFieldWithId>,
): FormSchema => {
  const schema: FormSchema = {
    question_type_id: {
      label: "Question Type",
      controlType: UIControlType.select,
      validation: { required: true },
      options: questionTypes.filter(qt => qt.isSystem),
      labelKey: "label",
      valueKey: "id",
      placeholder: "Select a Question Type",
      disabled: true,
    },
    input_label: {
      label: "Input Label",
      controlType: UIControlType.text,
      validation: { required: true },
    },
    description: {
      label: "Description",
      controlType: UIControlType.text,
      validation: { required: false },
    },
    question_width: {
      label: "Question Width",
      controlType: UIControlType.number,
      style: { display: "block" },
      placeholder: "Select Question Width",
    },
    options: {
      label: "Options",
      controlType: UIControlType.optionList,
      disabled: true,
    },
    forecast_days: {
      label: "Forecast Days",
      controlType: UIControlType.select,
      style: { display: "block" },
      validation: { required: true },
      options: Array.from({ length: 14 }, (_, i) => ({
        label: (i + 1).toString(),
        value: i + 1,
      })),
      placeholder: "Select Forecast Days",
    },
  };

  defaultSchemaData
    .filter((item: any) => item.dataSource === "QuestionFeature")
    .forEach((item: any) => {
      schema[item.fieldName] = {
        label: item.fieldLabel,
        controlType: item.controlType,
        style: { display: "none" },
      };

      if (item.description) {
        schema[item.fieldName].toolTipOptions = { message: item.description };
      }
    });

  schema.force_update = {
    controlType: UIControlType.checkbox,
    label: "Force Update",
    style: { display: "none" },
    toolTipOptions: {
      message:
        "When selected, this update will be made to all previously submitted inspection forms. When left un-selected this update will only apply to inspection forms moving forward.",
    },
  };

  return schema;
};

export const TemplateEditorProvider: React.FC<
  PropsWithChildren<{ templateId: number; groups: Array<PdfLayoutGroup> }>
> = ({ templateId, groups, children }) => {
  const queryClient = useQueryClient();
  const newQuestionGroupQuery = useCreateQuestionGroup(templateId);
  const questionTypesQuery = useQuestionTypes();
  const [defaultSchema, setDefaultSchema] = useState<FormSchema>({});
  const [questionsWithOptions, setQuestionsWithOptions] = useState<
    Array<number>
  >([]);
  const [questionConfigData, setQuestionConfigData] = useState<Array<any>>([]);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  const refetchQuestionGroups = () =>
    queryClient.invalidateQueries(pdfLayoutKey(templateId));

  const mappedAndSortedQuestionGroups = useMemo(
    () =>
      groups
        .map(({ id, order, name }) => ({ id, order, name }))
        .sort((a, b) => a.order - b.order),
    [groups],
  );

  const isConditionGroupApplied = mappedAndSortedQuestionGroups.some(
    group => group.name === "Condition Group",
  );

  // The template is expected to carry one question flagged as the inspection
  // type and one flagged as the inspection date.
  const warnings = useMemo<Array<WarningMessages>>(() => {
    const features = groups.flatMap(group =>
      group.questions.flatMap(q => q.features),
    );

    return [
      ...(features.includes(QuestionFeature.isInspectionType) ? [] : ["type"]),
      ...(features.includes(QuestionFeature.isInspectionDate) ? [] : ["date"]),
    ] as Array<WarningMessages>;
  }, [groups]);

  const addNewQuestionGroup = async (
    newQuestionGroup: NewQuestionGroup,
  ): Promise<{ id: number }> => {
    return new Promise((yes, no) => {
      newQuestionGroupQuery.mutate(newQuestionGroup, {
        onSuccess: r => {
          yes(r);
          refetchQuestionGroups();
        },
        onError: no,
      });
    });
  };

  const swapQuestionGroups = async (groupId: number, direction: string) => {
    const index = mappedAndSortedQuestionGroups.findIndex(
      _ => _.id === groupId,
    );
    const other =
      mappedAndSortedQuestionGroups[direction === "up" ? index - 1 : index + 1];

    if (index >= 0 && other) {
      await questionGroupApi.swap(templateId, groupId, other.id);
      await refetchQuestionGroups();
    }
  };

  const getNextQuestionGroupOrder = () =>
    mappedAndSortedQuestionGroups.length > 0
      ? Math.max(...mappedAndSortedQuestionGroups.map(_ => _.order)) + 1
      : 1;

  useEffect(() => {
    const loadData = async () => {
      try {
        const questionConfig = await questionConfigApi.index();

        setQuestionConfigData(questionConfig);
        setQuestionsWithOptions(
          questionConfig.reduce(
            (acc: any, curr: any) =>
              curr.fieldName === "options"
                ? acc.concat(curr.questionTypeId)
                : acc,
            [],
          ),
        );
        setDefaultSchema(
          buildSchema(questionTypesQuery.data || [], questionConfig),
        );
      } catch (err) {
        // Surface the failure instead of silently leaving questionConfigData empty.
        console.error("Failed to load question config", err);
      } finally {
        setIsConfigLoaded(true);
      }
    };

    if (questionTypesQuery.isFetched && questionTypesQuery.data) {
      loadData();
    }
  }, [questionTypesQuery.isFetched]);

  if (!isConfigLoaded) {
    return <Loading />;
  }

  return (
    <Context.Provider
      value={{
        questionTypes: questionTypesQuery.data || [],
        templateId,
        groups,
        refetchQuestionGroups,
        addNewQuestionGroup,
        defaultSchema,
        mappedAndSortedQuestionGroups,
        swapQuestionGroups,
        isConditionGroupApplied,
        questionsWithOptions,
        questionConfigData,
        getNextQuestionGroupOrder,
        warnings,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
